import React, { useState, useEffect, useRef } from "react";
import {
  useNearbyViewDispatch,
  useNearbyViewContext,
  useStoreAppValue,
  useToggle,
  useWindowSize,
} from "@hooks";
import { useCombobox } from "downshift";
import styled from "@emotion/styled";
import debounce from "lodash/debounce";
import { constants } from "@services";
import { Box, SearchIcon, Text, Flex, FontAwesome, TouchableOpacity, H2 } from "@atoms";
import NearbySearchResultQuery from "../NearbySearchResult/NearbySearchResultQuery";
import { NearbyLocationSearch } from "../NearbyLocationSearch";
import { NearbyDepartmentSearch } from "../NearbyDepartmentSearch";
import { BrandListLinkTypeFilters } from "../../brandListComponents";
import { Breakpoint } from "react-socks";
import { NearbyMap } from "../NearbyMap";
import { navigate } from "@reach/router";
import queryString from "query-string";

// == Types ================================================================

interface IProps {
  location: $FixMe;
}

// == Constants ============================================================

NearbySearch.defaultProps = {};

// == Component ============================================================

export default function NearbySearch({ location }: IProps) {
  const { currency } = useStoreAppValue();
  const [mapView, setMapView] = useToggle(false);
  const [nearbyViewState, nearbyViewDispatch] = useNearbyViewContext();
  const { department, departmentName } = nearbyViewState;
  const inputRef = useRef();
  const [inputValue, setInputValue] = useState("");
  const [brandName, setBrandName] = useState(null);
  const queryParams = queryString.parse(location.search);
  const params = Object.keys(queryParams);
  const { width } = useWindowSize();

  useEffect(() => {
    if (params.includes("b")) {
      const brandName = queryParams["brand"] as string;
      setBrandName(brandName);
    }
    if (width > 768) {
      inputRef?.current.focus();
    }
  }, [location]);

  const { linkTypeFilter, linkTypes } = nearbyViewState;

  const onStateChange = debounce(({ inputValue: newInputValue }) => {
    if (typeof newInputValue !== "undefined") {
      setInputValue(newInputValue);
      nearbyViewDispatch({ type: "UPDATE_QUERY", query: newInputValue });
      navigate(location.pathname);
    }
  }, 300);

  const stateReducer = (state, actionAndChanges) => {
    switch (actionAndChanges.type) {
      case useCombobox.stateChangeTypes.InputChange:
        setBrandName(null);
        onStateChange({ inputValue: actionAndChanges.changes.inputValue });
        return {
          ...actionAndChanges.changes,
          inputValue: actionAndChanges.changes.inputValue,
        };
      default:
        return actionAndChanges.changes;
    }
  };

  const { getInputProps, getComboboxProps } = useCombobox({
    items: [],
    onInputValueChange: ({ inputValue }) => {
      setInputValue(inputValue);
    },
    stateReducer,
  });

  // can be in context provider
  const onSelectCallback = (latitude: number, longitude: number, address: string) => {
    nearbyViewDispatch({
      type: "UPDATE_LOCATION",
      coordinates: { latitude, longitude, address },
    });
  };

  const onDepartmentSelect = (department: string | null) => {
    nearbyViewDispatch({ type: "UPDATE_DEPARTMENT", department: department });
  };

  const onMapViewToggle = () => {
    setMapView(!mapView);
  };

  const searchInputValue = () => {
    if (department && inputValue === "") {
      return departmentName;
    }
    if (brandName) {
      return brandName;
    }
    return undefined;
  };

  return (
    <Flex position="relative" height="100%" flexDirection="column">
      <Box m={3} mb={currency === "USD" ? 0 : 3} position="relative">
        <Flex>
          <Box flex={1}>
            <Box {...getComboboxProps()} position="relative">
              {inputValue === "" && (
                <NearbyDepartmentSearch onDepartmentSelect={onDepartmentSelect} />
              )}
              <EText color="primary">
                <SearchIcon />
              </EText>
              <ESearchInput
                placeholder={constants.SEARCH_PLACEHOLER}
                {...getInputProps()}
                value={searchInputValue()}
                ref={inputRef}
              />
            </Box>
            <Flex>
              <NearbyLocationSearch onSelectCallback={onSelectCallback} />
            </Flex>
          </Box>
          <Breakpoint small down>
            <Flex alignItems="center" borderLeft="none" border="silver" height="100%" p={2}>
              <TouchableOpacity onTouch={onMapViewToggle}>
                <Text color="primary">{mapView ? "List" : "Map"}</Text>
              </TouchableOpacity>
            </Flex>
          </Breakpoint>
        </Flex>
        {currency === "USD" && (
          <BrandListLinkTypeFilters
            nearbySearch={true}
            dispatch={nearbyViewDispatch}
            linkTypeFilter={linkTypes.length > 0 ? linkTypes[0] : null}
            p={3}
            mb={0}
          />
        )}
      </Box>
      <EBreakpoint small down>
        {mapView && <NearbyMap mapView />}
        <EResultsContainer display={mapView ? "none" : "block"}>
          <NearbySearchResultQuery location={location} />
        </EResultsContainer>
      </EBreakpoint>
      <EBreakpoint medium up>
        <EResultsContainer>
          <NearbySearchResultQuery location={location} />
        </EResultsContainer>
      </EBreakpoint>
    </Flex>
  );
}

// == Styles ===============================================================

const EResultsContainer = styled(Box)`
  background: white;
  border-top: ${({ theme }) => `1px solid ${theme.colors.gray}`};
`;

const EText = styled(Text)`
  position: absolute;
  left: 15px;
  top: 13px;
`;

const ESearchInput = styled.input`
  margin-bottom: 0 !important;
  height: 40px !important;
  border-radius: 0 !important;
  border-color: #ccc !important;
  padding-top: 0 !important;
  border-bottom: 0 !important;
  padding-left: 40px !important;
`;

const EBreakpoint = styled(Breakpoint)`
  height: 100%;
  overflow-y: auto;
`;
